import { DocumentTemplateType, INumberedDocumentView } from '../../api-client/autogenerated';
import { getDocumentNameDate, getDocumentNumberText } from '../../scripts/utils';

const clean = (s?: string | null) => (s && s !== 'N/A' ? s : '');

/** The document types My Work titles by their date (the date is part of their name). */
const DATED_TYPES = [
  DocumentTemplateType.ContractorDailyLogs,
  DocumentTemplateType.FieldReports,
  DocumentTemplateType.MeetingMinutes,
  DocumentTemplateType.DesignMeetingMinutes,
];

type TypeOf = (doc: INumberedDocumentView) => DocumentTemplateType | undefined;

/**
 * The bold identifier column of My Work: the same number the document's name has, so the page,
 * notifications and emails agree. A punch list item shows its location instead.
 */
export const getMyWorkNumber = (doc: INumberedDocumentView, typeOf: TypeOf): string => {
  if (doc.additionalReviewForDocument)
    return getMyWorkNumber(doc.additionalReviewForDocument as INumberedDocumentView, typeOf);
  const t = typeOf(doc);
  if (t === DocumentTemplateType.PunchList) return clean(doc.physicalLocation?.name);
  return getDocumentNumberText(doc, t as DocumentTemplateType) || 'N/A';
};

/**
 * The two-line title column: top line + secondary line per type (title over description;
 * submittals show the section description over the title; dated types lead with their date).
 */
export const getMyWorkTitleLines = (
  doc: INumberedDocumentView,
  typeOf: TypeOf,
): { top: string; bottom: string } => {
  if (doc.additionalReviewForDocument)
    return getMyWorkTitleLines(doc.additionalReviewForDocument as INumberedDocumentView, typeOf);
  const t = typeOf(doc);
  switch (t) {
    case DocumentTemplateType.Submittals:
    case DocumentTemplateType.CloseoutSubmittals:
    case DocumentTemplateType.SubmittalPackages:
    case DocumentTemplateType.CloseoutSubmittalPackages:
      return { top: clean(doc.submittalSectionDescription), bottom: clean(doc.title) };
    default: {
      const date = DATED_TYPES.includes(t as DocumentTemplateType) ? getDocumentNameDate(doc) : '';
      if (date) return { top: date, bottom: clean(doc.title) || clean(doc.description) };
      return { top: clean(doc.title), bottom: clean(doc.description) };
    }
  }
};
